import { SeedingStrategy } from "features/WorklistTools/shared/interfaces";
import {
  calculateSpinParamGForce,
  getRowAndColCountByLabwareType,
  getSourcePlateInfo,
  getValueInDetailsString,
} from "features/WorklistTools/shared/WorklistHelpers";
import ExtThawWorklistService, {
  GetASAMAndPlateInfoData,
} from "services/ExtThawWorklistToolService";
import {
  DestPlateInfoState,
  IntermediatePlateInfoState,
  SourcePlateInfoState,
} from "../../state";
import {
  getIntermediatePlateInfoState,
  getSourcePlateInfoState,
} from "../../state-helpers";
import { IExternalThawMethodSettings } from "../../state/initial-state";
export interface UploadedWorklist {
  task: string;
  details: string;
  aspiratevol: string;
  dispensevol: string;
  transfervol: string;
  sourceplatetype: string;
  sourceplatebarcode: string;
  sourcewellid: string;
  destinationplatetype: string;
  destinationplatebarcode: string;
  destinationwellid: string;
  rownumber: string;
}

export const parsePlateInfo = (
  uploadedWorklist: UploadedWorklist[],
  key: keyof UploadedWorklist
) => {
  return [
    ...new Map(uploadedWorklist.map((rows) => [rows[key], rows])).values(),
  ];
};

const mapWorklistRows = (
  row: UploadedWorklist,
  sourcePlateIndex: number,
  destPlateIndex: number
) => {
  return {
    sourcePlateIndex: sourcePlateIndex,
    sourceWellId: row.sourcewellid,
    destPlateIndex: destPlateIndex,
    destWellId: row.destinationwellid,
    transferVol: row.transfervol ?? "",
  };
};

export const parseUploadedWorklist = async (
  worklistRows: UploadedWorklist[]
) => {
  const methodDataString =
    worklistRows.find((e) => e.task === "MethodData")?.details ?? "";

  const methodReservationId = getValueInDetailsString(
    methodDataString,
    "MethodReservationId"
  );

  const { data: asamAndPlateData } =
    await ExtThawWorklistService.getASAMAndPlateInfo(methodReservationId);

  const methodSettings = parseMethodSettingsFromWorklist(
    worklistRows,
    asamAndPlateData
  );

  const harvestWellsRows = worklistRows.filter(
    (e) => e.task === "IntermediatePlate"
  );
  const int1ToDestRows = worklistRows.filter(
    (e) => e.task === "DestinationPlate"
  );
  const sourcePlateInfo: SourcePlateInfoState[] = Array.from(
    { length: 2 },
    () => getSourcePlateInfoState()
  );
  const intPlateInfo: IntermediatePlateInfoState[] = Array.from(
    { length: 1 },
    () => getIntermediatePlateInfoState()
  );
  const destPlateInfo: DestPlateInfoState[] = [
    {
      index: 0,
      plateBarcode: "",
      labwareTypeCode: "",
      operatingVol: 0,
      minOperatingVol: 0,
      rows: [],
      cols: [],
      preprocess: true,
      topup: true,
      topUpVol: 0,
      startsInIncubator: true,
      endsInIncubator: true,
    },
  ];

  const uniqueSourcePlates = parsePlateInfo(
    harvestWellsRows,
    "sourceplatebarcode"
  );

  const uniqueIntermediatePlates = parsePlateInfo(
    harvestWellsRows,
    "destinationplatebarcode"
  );

  const uniqueDestinationPlates = parsePlateInfo(
    [...int1ToDestRows],
    "destinationplatebarcode"
  );

  for (const [index, row] of uniqueSourcePlates.entries()) {
    const wellInfo = await getSourcePlateInfo(0, row.sourceplatebarcode, false);
    const rowColCount = getRowAndColCountByLabwareType(row.sourceplatetype);
    const sourcePlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareTypeId === 1 &&
        plate.labwareTypeCode === row.sourceplatetype
    );

    sourcePlateInfo[index] = {
      plateBarcode: row.sourceplatebarcode,
      labwareTypeCode: row.sourceplatetype,
      operatingVol:
        sourcePlateData?.maxLabwareVolume ?? rowColCount.operatingVol,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      wellInfo: wellInfo,
    };
  }

  const getIntWellVol = (row: UploadedWorklist) => {
    const resuspensionVol = parseInt(row?.dispensevol ?? "0");
    const intWellVol =
      resuspensionVol -
      methodSettings.numberOfAliquots * methodSettings.aliquotVolume;
    return intWellVol;
  };

  for (const [index, row] of uniqueIntermediatePlates.entries()) {
    const intPlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareTypeId === 3 &&
        plate.labwareTypeCode === row.destinationplatetype
    );

    intPlateInfo[index] = {
      plateBarcode: row.destinationplatebarcode,
      labwareTypeCode: row.destinationplatetype ?? "",
      rows: [1, 2, 3, 4, 5, 6, 7, 8],
      cols: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      operatingVol: (intPlateData?.maxLabwareVolume ?? 2000) * 0.9,
      resuspensionVol: parseInt(row?.dispensevol ?? "0"),
      intWellVol: getIntWellVol(row),
    };
  }

  for (const [index, row] of uniqueDestinationPlates.entries()) {
    const rowColCount = getRowAndColCountByLabwareType(
      row.destinationplatetype
    );

    const destPlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareTypeId === 2 &&
        plate.labwareTypeCode === row.destinationplatetype
    );

    destPlateInfo[index] = {
      index: index,
      plateBarcode: row.destinationplatebarcode,
      labwareTypeCode: row.destinationplatetype,
      operatingVol: destPlateData?.maxLabwareVolume ?? rowColCount.operatingVol,
      minOperatingVol: destPlateData?.maxLabwareVolume ?? 200,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      preprocess: !!parseInt(
        getValueInDetailsString(row.details, "Preprocess")
      ),
      topup: !!parseInt(getValueInDetailsString(row.details, "Topup")),
      topUpVol: parseInt(
        getValueInDetailsString(row.details, "TopupVolume") ?? "0"
      ),
      startsInIncubator: !!parseInt(
        getValueInDetailsString(row.details, "StartInIncubator")
      ),
      endsInIncubator: !!parseInt(
        getValueInDetailsString(row.details, "EndInIncubator")
      ),
    };
  }

  const deadPlate = worklistRows.find((row) => row.task === "DeadTotalPlate");
  const deadPlateBarcode = deadPlate?.destinationplatebarcode;
  const deadPlateType = deadPlate?.destinationplatetype;
  const harvestWells = harvestWellsRows.map((row) =>
    mapWorklistRows(
      row,
      sourcePlateInfo.findIndex(
        (plate) => plate.plateBarcode === row.sourceplatebarcode
      ),
      intPlateInfo.findIndex(
        (e) => e.plateBarcode === row.destinationplatebarcode
      )
    )
  );

  const getDestPlateIndex = (row: UploadedWorklist) =>
    uniqueDestinationPlates.findIndex(
      (e) => e.destinationplatebarcode === row.destinationplatebarcode
    );
  const int1ToDest = int1ToDestRows.map((row) =>
    mapWorklistRows(
      row,
      intPlateInfo.findIndex((e) => e.plateBarcode === row.sourceplatebarcode),
      getDestPlateIndex(row)
    )
  );

  const parsedWorklistInfo = {
    harvestWells,
    int1ToDest,
    sourcePlateInfo,
    intPlateInfo,
    destPlateInfo,
    deadPlateBarcode,
    deadPlateType,
    methodSettings,
  };

  return parsedWorklistInfo;
};

const parseMethodSettingsFromWorklist = (
  uploadedWorklist: UploadedWorklist[],
  asamAndPlateData: GetASAMAndPlateInfoData
) => {
  const getWorklistRowByTask = (task: string) =>
    uploadedWorklist.find((e) => e.task === task);

  const getTaskTransferVol = (task: string) =>
    parseInt(getWorklistRowByTask(task)?.transfervol ?? "0");

  const intCentrifugationString =
    getWorklistRowByTask("IntermediateCentrifugation")?.details ?? "";

  const deadTotalPlateString =
    getWorklistRowByTask("DeadTotalPlate")?.details ?? "";

  const seedingStrategyPlateString =
    getWorklistRowByTask("SeedingStrategy")?.details ?? "";

  const getSeedingStrategy = () => {
    const seedingStratString = getValueInDetailsString(
      seedingStrategyPlateString,
      "SeedingStrat",
      ""
    );

    return seedingStratString === "Targeted"
      ? SeedingStrategy.CellCount
      : SeedingStrategy.Volumetric;
  };

  const methodSettings: IExternalThawMethodSettings = {
    selectedSystem: asamAndPlateData.system ?? 3,
    arraySystemAutomationMethodId:
      asamAndPlateData.arraySystemAutomationMethodId ?? 396,
    numberOfAliquots: parseInt(
      getValueInDetailsString(deadTotalPlateString, "AliquotNumber")
    ),
    aliquotVolume: getTaskTransferVol("DeadTotalPlate"),
    spinParamGForce: calculateSpinParamGForce(
      parseInt(getValueInDetailsString(intCentrifugationString, "VSpinRPM"))
    ),
    spinParamPercent: parseInt(
      getValueInDetailsString(intCentrifugationString, "VSpinRPM")
    ),
    spinTime: parseInt(
      (
        parseInt(
          getValueInDetailsString(intCentrifugationString, "VSpinTime")
        ) / 60
      ).toString()
    ),
    spinAccel: parseInt(
      getValueInDetailsString(intCentrifugationString, "VSpinAccel")
    ),
    spinDecel: parseInt(
      getValueInDetailsString(intCentrifugationString, "VSpinDecel")
    ),
    defaultVSpinSettings: false,
    seedingStrategy: getSeedingStrategy(),
    splitSeedingVolEvenly: false,
    experimentSettings: getValueInDetailsString(
      deadTotalPlateString,
      "ExperimentSettings",
      ""
    ),
    batchDestPlates: false,
    incubationTime: parseInt(
      getValueInDetailsString(deadTotalPlateString, "IncubationTime")
    ),
  };
  return methodSettings;
};
